/**
 * @license Codistica
 *
 * Copyright (c) 2020, Codistica and its affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE.md file in the root directory of this source tree.
 *
 * BUILD DATE: Fri Jul 10 2020 16:51:08 GMT+0200 (Central European Summer Time)
 */
import _mapInstanceProperty from "@babel/runtime-corejs3/core-js-stable/instance/map";
import _slicedToArray from "@babel/runtime-corejs3/helpers/esm/slicedToArray";
import _bindInstanceProperty from "@babel/runtime-corejs3/core-js-stable/instance/bind";
import _newArrowCheck from "@babel/runtime-corejs3/helpers/esm/newArrowCheck";

/** @module react/components/track-slide */
import React, { useState, useCallback, useEffect } from 'react';
import { animated, useSpring } from 'react-spring';
import resetClassNames from '../../css/reset.module.scss';
import { mergeClassNames } from '../../modules/merge-class-names.js';
import { mergeStyles } from '../../modules/merge-styles.js';
import componentClassNames from './index.module.scss'; // TODO: SUPPORT ADJUST SLIDE SIZE TO CURRENT ITEM SIZE (TO LEAVE SCROLLING TO DOCUMENT) (MAKE A SEPARATE COMPONENT?)
// TODO: ADD OPTION TO MAKE INITIAL ANIMATION (LIKE TracklessSlide).
// TODO: ADD OPTION TO NOT ADD INTERNAL DIMENSION STYLES.
// TODO: ADD DRAG SUPPORT VIA draggable BOOLEAN PROP (ONLY IF POSSIBLE) (USE react-use-gesture).
// TODO: ADD swipe SUPPORT IF DRAG SUPPORT IS NOT POSSIBLE.

/**
 * @typedef trackSlideDimensionsType
 * @property {(string|number)} height - Slide height.
 * @property {(string|number)} width - Slide width.
 * @property {(string|number)} [minHeight=null] - Slide minimum height.
 * @property {(string|number)} [minWidth=null] - Slide minimum width.
 * @property {(string|number)} [maxHeight=null] - Slide maximum height.
 * @property {(string|number)} [maxWidth=null] - Slide maximum width.
 */

/**
 * @typedef trackSlidePropsType
 * @property {string} [direction='row'] - Slide direction.
 * @property {number} [startingPosition=0] - Slide starting position.
 * @property {number} [itemsPerView=1] - Slide items per view.
 * @property {trackSlideDimensionsType} [dimensions] - Slide dimensions.
 * @property {Function} [onMount=null] - Callback for componentDidMount event.
 * @property {Function} [onNewPosition=null] - Callback for newPosition event.
 * @property {*} [children=null] - React prop.
 * @property {Object<string,*>} [style={}] - React prop.
 * @property {string} [className=''] - React prop.
 * @property {Object<string,*>} [customStyles={}] - Custom styles prop.
 * @property {Object<string,*>} [customClassNames={}] - Custom classNames prop.
 * @property {('default'|string|null)} [globalTheme='default'] - Global theme to be used.
 */

/**
 * @description A simple yet powerful track slide component.
 * @param {trackSlidePropsType} props - Component props.
 * @returns {Object<string,*>} React component.
 */
function TrackSlide(props) {
  var _this = this,
      _context,
      _context2,
      _context3,
      _context5,
      _context7,
      _context8,
      _context9,
      _context10;

  var direction = props.direction,
      startingPosition = props.startingPosition,
      itemsPerView = props.itemsPerView,
      dimensions = props.dimensions,
      onMount = props.onMount,
      onNewPosition = props.onNewPosition,
      children = props.children,
      style = props.style,
      className = props.className,
      customStyles = props.customStyles,
      customClassNames = props.customClassNames,
      globalTheme = props.globalTheme;

  var _ref = dimensions || {},
      _ref$height = _ref.height,
      height = _ref$height === void 0 ? 100 : _ref$height,
      _ref$width = _ref.width,
      width = _ref$width === void 0 ? 100 : _ref$width,
      _ref$minHeight = _ref.minHeight,
      minHeight = _ref$minHeight === void 0 ? null : _ref$minHeight,
      _ref$minWidth = _ref.minWidth,
      minWidth = _ref$minWidth === void 0 ? null : _ref$minWidth,
      _ref$maxHeight = _ref.maxHeight,
      maxHeight = _ref$maxHeight === void 0 ? null : _ref$maxHeight,
      _ref$maxWidth = _ref.maxWidth,
      maxWidth = _ref$maxWidth === void 0 ? null : _ref$maxWidth;

  var clampPosition = useCallback(_bindInstanceProperty(_context = function _context(value) {
    _newArrowCheck(this, _this);

    if (value + itemsPerView >= children.length) {
      value = children.length - itemsPerView;
    } else if (value < 0) {
      value = 0;
    }

    return value;
  }).call(_context, this), [children.length]);

  var _useState = useState({
    position: clampPosition(startingPosition)
  }),
      _useState2 = _slicedToArray(_useState, 2),
      state = _useState2[0],
      setState = _useState2[1];

  var isRow = direction === 'row';
  var goTo = useCallback(_bindInstanceProperty(_context2 = function _context2(newPosition) {
    _newArrowCheck(this, _this);

    newPosition = clampPosition(newPosition);
    onNewPosition && onNewPosition(newPosition);
    setState({
      position: newPosition
    });
  }).call(_context2, this), [clampPosition]);
  var next = useCallback(_bindInstanceProperty(_context3 = function _context3(group) {
    var _this2 = this,
        _context4;

    _newArrowCheck(this, _this);

    return setState(_bindInstanceProperty(_context4 = function _context4(prevState) {
      _newArrowCheck(this, _this2);

      var delta = group ? itemsPerView : 1;
      var newPosition = clampPosition(prevState.position + delta);
      onNewPosition && onNewPosition(newPosition);
      return {
        position: newPosition
      };
    }).call(_context4, this));
  }).call(_context3, this), [itemsPerView, clampPosition]);
  var previous = useCallback(_bindInstanceProperty(_context5 = function _context5(group) {
    var _this3 = this,
        _context6;

    _newArrowCheck(this, _this);

    return setState(_bindInstanceProperty(_context6 = function _context6(prevState) {
      _newArrowCheck(this, _this3);

      var delta = group ? -itemsPerView : -1;
      var newPosition = clampPosition(prevState.position + delta);
      onNewPosition && onNewPosition(newPosition);
      return {
        position: newPosition
      };
    }).call(_context6, this));
  }).call(_context5, this), [itemsPerView, clampPosition]);
  var getTransformValue = useCallback(_bindInstanceProperty(_context7 = function _context7(value) {
    _newArrowCheck(this, _this);

    if (isRow) {
      return "translateX(".concat(value, "%)");
    } else {
      return "translateY(".concat(value, "%)");
    }
  }).call(_context7, this), [isRow]);
  var globalStyles = globalTheme ? TrackSlide.globalStyles[globalTheme] || {} : {};
  var globalClassNames = globalTheme ? TrackSlide.globalClassNames[globalTheme] || {} : {};
  var mergedStyles = {
    root: mergeStyles(globalStyles.root, customStyles.root, style, {
      height: height,
      width: width,
      minHeight: minHeight,
      minWidth: minWidth,
      maxHeight: maxHeight,
      maxWidth: maxWidth
    }),
    track: mergeStyles(globalStyles.track, customStyles.track, {
      height: '100%',
      width: '100%',
      minHeight: null,
      minWidth: null,
      maxHeight: null,
      maxWidth: null,
      display: 'flex',
      flexDirection: direction
    }),
    item: mergeStyles(globalStyles.item, customStyles.item, {
      height: !isRow ? "".concat(100 / itemsPerView, "%") : '100%',
      width: isRow ? "".concat(100 / itemsPerView, "%") : '100%',
      minHeight: null,
      minWidth: null,
      maxHeight: null,
      maxWidth: null
    })
  };
  var mergedClassNames = {
    root: mergeClassNames(resetClassNames.root, componentClassNames.root, globalClassNames.root, customClassNames.root, className),
    track: mergeClassNames(componentClassNames.track, globalClassNames.track, customClassNames.track),
    item: mergeClassNames(componentClassNames.item, globalClassNames.item, customClassNames.item)
  };
  var springProps = useSpring({
    translateValue: -100 * state.position / itemsPerView
  });
  useEffect(_bindInstanceProperty(_context8 = function _context8() {
    _newArrowCheck(this, _this);

    onMount && onMount({
      goTo: goTo,
      next: next,
      previous: previous
    });
  }).call(_context8, this), []);
  return /*#__PURE__*/React.createElement("div", {
    style: mergedStyles.root,
    className: mergedClassNames.root
  }, /*#__PURE__*/React.createElement(animated.div, {
    style: mergeStyles(mergedStyles.track, {
      transform: springProps.translateValue.interpolate(_bindInstanceProperty(_context9 = function _context9(val) {
        _newArrowCheck(this, _this);

        return getTransformValue(val);
      }).call(_context9, this))
    }),
    className: mergedClassNames.track
  }, _mapInstanceProperty(children).call(children, _bindInstanceProperty(_context10 = function _context10(child, index) {
    _newArrowCheck(this, _this);

    return /*#__PURE__*/React.createElement("div", {
      key: index,
      style: mergedStyles.item,
      className: mergedClassNames.item
    }, child);
  }).call(_context10, this))));
}

TrackSlide.defaultProps = {
  direction: 'row',
  startingPosition: 0,
  itemsPerView: 1,
  onMount: null,
  onNewPosition: null,
  children: null,
  style: {},
  className: '',
  customStyles: {},
  customClassNames: {},
  globalTheme: 'default'
};
TrackSlide.globalStyles = {
  default: {
    root: {},
    track: {},
    item: {}
  }
};
TrackSlide.globalClassNames = {
  default: {
    root: '',
    track: '',
    item: ''
  }
};
export { TrackSlide };